/* eslint-disable */
import type { KeyValueMap } from "../../../xyz/block/genie/v1/state.pb";
import type {
  CashLiteApplePayPushProvisioningInputs,
  CashLiteGooglePayPushProvisioningInputs,
} from "../../cash/cashliteflow/blockers/v1beta1/push_provisioning_blockers.pb";
import type { SubmitAutomationForFlowRequest } from "../../cash/investautomator/api/flows/submitAutomationForFlow.pb";
import type { SubmitInvestmentEntitySelectionRequest } from "../../cash/investflow/flows/actions.pb";
import type { SelectInstrumentRequest } from "../../cash/moneta/api/v1_0/plasma/app.pb";
import type { SetPasswordRequest, VerifyPasswordRequest } from "../../cash/security/api.pb";
import type { Profile } from "../ProfileProto.pb";
import type { RequestContext } from "../RequestContextProto.pb";
import type { ResponseContext } from "../ResponseContextProto.pb";
import type { SetPasscodeRequest } from "../SetPasscodeRequestProto.pb";
import type { SyncEntitiesResponse } from "../SyncEntitiesResponseProto.pb";
import type { Blockers } from "../blockers.pb";
import type { ResolvePersonaDidvBlockerRequest } from "../persona_didv.pb";
import type { CheckDepositRequest } from "../rpc/check_deposit.pb";
import type { HandleThreeDomainSecureRedirectRequest } from "../rpc/handle_three_domain_secure_redirect.pb";
import type { HandleThreeDomainSecureV2ActionRequest } from "../rpc/handle_three_domain_secure_v2_action.pb";
import type { ScenarioPlan } from "../scenarios.pb";
import type { StatusResult } from "../status_result.pb";
import type { SelectActivityRequest } from "./activity.pb";
import type { AddMoneyRequest } from "./add_money.pb";
import type { SetAddressRequest } from "./address.pb";
import type { SetAmountRequest } from "./amount.pb";
import type { ProvisionApplePayRequest } from "./apple_pay.pb";
import type { SubmitBiometricsOptinRequest } from "./biometrics_optin.pb";
import type { SubmitBitcoinP2pConversionPercentageRequest } from "./bitcoin_p2p_conversion.pb";
import type { LinkCardRequest } from "./card.pb";
import type { SetCardCustomizationRequest } from "./card_customization.pb";
import type { SetCashtagRequest } from "./cashtag.pb";
import type { ClabeEntryRequest } from "./clabe.pb";
import type { VerifyContactsRequest } from "./contacts.pb";
import type { SetCountryRequest } from "./country.pb";
import type { CreditMultiStepLoadPollingRequest, FinishCreditMultiStepLoadRequest } from "./credit_multi_step_load.pb";
import type { SetDateRequest } from "./date.pb";
import type {
  ActivateDigitalWalletRequest,
  CompleteDigitalWalletTokenProvisioningRequest,
  ProvisionDigitalWalletTokenRequest,
} from "./digital_wallet_token.pb";
import type { ConfirmDisclosureRequest } from "./disclosure.pb";
import type { RegisterEmailRequest, VerifyEmailRequest } from "./email.pb";
import type { SubmitFilesetRequest } from "./fileset.pb";
import type { EndFlowRequest } from "./flow.pb";
import type { SetFullNameRequest } from "./full_name.pb";
import type { HumanConfirmationRequest } from "./human_confirmation.pb";
import type { VerifyIdentityRequest } from "./identity.pb";
import type { VerifyInstrumentRequest } from "./instrument.pb";
import type { SubmitLiteRecoveryEmailOtpBlockerRequest } from "./lite_recovery_email_otp.pb";
import type { LoanAmountPickerRequest } from "./loan_amount_picker.pb";
import type { ResolveMergeRequest } from "./merge.pb";
import type { MultiCurrencyAmountEntryRequest, MultiCurrencyPaymentReviewRequest } from "./multi_currency.pb";
import type { OverflowOptionPickerRequest } from "./overflow_option_picker.pb";
import type { P2pInstrumentSelectionRequest } from "./p2p_instrument_selection.pb";
import type { SelectParticipantRequest } from "./participant.pb";
import type { VerifyPasscodeAndExpirationRequest, VerifyPasscodeRequest } from "./passcode.pb";
import type { SubmitPasskeyAuthBlockerRequest } from "./passkey_auth.pb";
import type { ConfirmPaymentRequest } from "./payment.pb";
import type { PaymentDeviceCustomizationRequest } from "./payment_device_customization.pb";
import type { SubmitFidesmoProvisioningBlockerRequest } from "./payment_token_device.pb";
import type { SubmitPayrollProviderSelectionRequest } from "./payroll_provider_selection.pb";
import type { SubmitProveMobileAuthBlockerRequest } from "./prove_mobile_auth.pb";
import type { VerifyQrCodeRequest } from "./qr_code.pb";
import type { SetRatePlanRequest } from "./rate_plan.pb";
import type { RequestPushNotificationsRequest } from "./request_push_notifications.pb";
import type { ApplyRewardCodeRequest } from "./reward_code.pb";
import type { SetScheduledTransactionPreferenceRequest } from "./scheduled_transaction.pb";
import type { SelectOptionRequest } from "./select_option.pb";
import type { SelectTransferOptionRequest } from "./select_transfer_option.pb";
import type { SetSignatureRequest } from "./signature.pb";
import type { RegisterSmsRequest, VerifySmsRequest } from "./sms.pb";
import type { SelectSponsorsRequest } from "./sponsors.pb";
import type { SubmitFormRequest } from "./submit_form.pb";
import type { FinishTutorialRequest } from "./tutorial.pb";
import type { UploadFileRequest } from "./upload_file.pb";

export interface SkipBlockerRequest {
  request_context?: RequestContext;
  blockers?: Blockers;
}

export interface SkipBlockerResponse {
  response_context?: ResponseContext;
  status?: SkipBlockerResponseStatus;
}

export enum SkipBlockerResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CONCURRENT_MODIFICATION - The blocker provided cannot be skipped, either because it is unavailable, or not
   * possible to skip in this flow.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/**
 * Sent by the client to submit one or more blocker inputs.
 * Will advance the flow to the next blocker(s).
 * Not idempotent -- duplicate requests will fail due to the flow having already advanced.
 *
 * All new blockers should use this API rather than a new endpoint.
 * See also: https://dev-guides.sqprod.co/cash/docs/product_velocity/plasma/features/custom_blockers
 *
 * This API is used to encapsulate groups of blockers which are submitted together.
 * See also: https://cash-dev-guide.squarecloudservices.com/product_velocity/plasma/features/migrating_to_multi_blocker/
 *
 * Ongoing migration: This API is slowly becoming the envelope for all blocker requests.
 * It will eventually obviate all other blocker input APIs.
 */
export interface SubmitBlockerRequest {
  /**
   * A server-provided string that identifies a group of blockers.
   * The client will have received this when it received the blockers in a previous request.
   */
  group_id?: string;
  app_request_context?: SubmitBlockerRequestAppRequestContext;
  /** One or more blocker input requests bundled into a list */
  requests?: SubmitBlockerRequestRequest[];
}

/** Contextual information about the state of the client making this request. */
export interface SubmitBlockerRequestAppRequestContext {
  /**
   * Metadata the server will use to fetch sync entities.
   * Use this field as the source of truth regarding known sync ranges,
   * as the sub-requests in the oneof field will also have all_known_ranges fields.
   */
  all_known_ranges?: string[];
}

/**
 * The actual contents of a single blocker input request.
 * For simplicity, this is simply one of the existing blocker input API request objects.
 * In the future this might be turned into something with less legacy baggage.
 */
export interface SubmitBlockerRequestRequest {
  /** This overrides the request_context within each request type below. */
  blocker_descriptor_id?: string;
  /** EmailVerificationBlocker */
  verify_email_request?:
    | VerifyEmailRequest
    | undefined;
  /** PasscodeVerificationBlocker */
  verify_passcode_request?:
    | VerifyPasscodeRequest
    | undefined;
  /** InstrumentVerificationBlocker */
  verify_instrument_request?:
    | VerifyInstrumentRequest
    | undefined;
  /** PasscodeCreationBlocker */
  set_passcode_request?:
    | SetPasscodeRequest
    | undefined;
  /** CardBlocker */
  link_card_request?:
    | LinkCardRequest
    | undefined;
  /** IdentityVerificationBlocker */
  verify_identity_request?:
    | VerifyIdentityRequest
    | undefined;
  /**
   * RatePlanBlocker
   *
   * @deprecated
   */
  set_rate_plan_request?:
    | SetRatePlanRequest
    | undefined;
  /** PhoneVerificationBlocker */
  verify_sms_request?:
    | VerifySmsRequest
    | undefined;
  /** ConfirmBlocker */
  confirm_payment_request?:
    | ConfirmPaymentRequest
    | undefined;
  /** NameBlocker */
  set_full_name_request?:
    | SetFullNameRequest
    | undefined;
  /** CashtagBlocker */
  set_cashtag_request?:
    | SetCashtagRequest
    | undefined;
  /** ResolveMergeBlocker */
  resolve_merge_request?:
    | ResolveMergeRequest
    | undefined;
  /** RewardCodeBlocker */
  apply_reward_code_request?:
    | ApplyRewardCodeRequest
    | undefined;
  /** AddressBlocker */
  set_address_request?:
    | SetAddressRequest
    | undefined;
  /**
   * SelectionBlocker
   * ApplePaySelectionBlocker
   */
  select_option_request?:
    | SelectOptionRequest
    | undefined;
  /** ApplyPayProvisioningBlocker */
  provision_apple_pay_request?:
    | ProvisionApplePayRequest
    | undefined;
  /** SignatureBlocker */
  set_signature_request?:
    | SetSignatureRequest
    | undefined;
  /** QrCodeBlocker */
  verify_qr_code_request?:
    | VerifyQrCodeRequest
    | undefined;
  /**
   * CardPasscodeAndExpirationBlocker
   *
   * @deprecated
   */
  verify_passcode_and_expiration_request?:
    | VerifyPasscodeAndExpirationRequest
    | undefined;
  /** FileBlocker */
  upload_file_request?:
    | UploadFileRequest
    | undefined;
  /** ContactVerificationBlocker */
  verify_contacts_request?:
    | VerifyContactsRequest
    | undefined;
  /**
   * RegionBlocker
   *
   * @deprecated
   */
  set_country_request?:
    | SetCountryRequest
    | undefined;
  /** ScheduledTransactionBlocker */
  set_scheduled_transaction_preference_request?:
    | SetScheduledTransactionPreferenceRequest
    | undefined;
  /** GooglePayProvisioningBlocker */
  provision_digital_wallet_token_request?: ProvisionDigitalWalletTokenRequest | undefined;
  activate_digital_wallet_request?:
    | ActivateDigitalWalletRequest
    | undefined;
  /** CardCustomizationBlocker */
  set_card_customization_request?:
    | SetCardCustomizationRequest
    | undefined;
  /**
   * FormBlocker
   * WebviewBlocker
   */
  submit_form_request?:
    | SubmitFormRequest
    | undefined;
  /** DisclosureBlocker */
  confirm_disclosure_request?:
    | ConfirmDisclosureRequest
    | undefined;
  /** AliasBlocker */
  register_sms_request?: RegisterSmsRequest | undefined;
  register_email_request?:
    | RegisterEmailRequest
    | undefined;
  /** CheckDepositBlocker */
  check_deposit_request?:
    | CheckDepositRequest
    | undefined;
  /** ThreeDomainSecureRedirectBlocker */
  handle_three_domain_secure_redirect_request?:
    | HandleThreeDomainSecureRedirectRequest
    | undefined;
  /** AmountBlocker */
  set_amount_request?:
    | SetAmountRequest
    | undefined;
  /** GooglePayCompleteProvisioningBlocker */
  complete_digital_wallet_token_provisioning_request?:
    | CompleteDigitalWalletTokenProvisioningRequest
    | undefined;
  /**
   * TransactionPickerBlocker
   * ActivityPickerBlocker
   */
  select_activity_request?:
    | SelectActivityRequest
    | undefined;
  /** ThreeDomainSecureV2Blocker */
  handle_three_domain_secure_v2_action_request?:
    | HandleThreeDomainSecureV2ActionRequest
    | undefined;
  /** SelectSponsorsBlocker */
  select_sponsors_request?:
    | SelectSponsorsRequest
    | undefined;
  /** PasswordCreationBlocker */
  set_password_request?:
    | SetPasswordRequest
    | undefined;
  /** PasswordVerificationBlocker */
  verify_password_request?:
    | VerifyPasswordRequest
    | undefined;
  /** TutorialBlocker */
  finish_tutorial_request?:
    | FinishTutorialRequest
    | undefined;
  /** PayrollProviderSearchBlocker */
  submit_payroll_provider_selection_request?:
    | SubmitPayrollProviderSelectionRequest
    | undefined;
  /** PersonaDidvBlocker */
  resolve_persona_didv_blocker_request?:
    | ResolvePersonaDidvBlockerRequest
    | undefined;
  /**
   * RoundUpOnboardingBlocker
   * ChangeRoundUpDestinationBlocker
   */
  submit_automation_for_flow_request?:
    | SubmitAutomationForFlowRequest
    | undefined;
  /** FilesetUploadBlocker */
  submit_fileset_request?:
    | SubmitFilesetRequest
    | undefined;
  /** InstrumentSelectionBlocker */
  select_instrument_request?:
    | SelectInstrumentRequest
    | undefined;
  /** ParticipantSelectionBlocker */
  select_participant_request?:
    | SelectParticipantRequest
    | undefined;
  /** CreditMultiStepLoadingBlocker */
  finish_credit_multi_step_load_request?: FinishCreditMultiStepLoadRequest | undefined;
  credit_multi_step_polling_request?:
    | CreditMultiStepLoadPollingRequest
    | undefined;
  /** LoanAmountPicker */
  loan_amount_picker_request?:
    | LoanAmountPickerRequest
    | undefined;
  /** SelectInvestmentEntityBlocker */
  submit_investment_entity_selection_request?:
    | SubmitInvestmentEntitySelectionRequest
    | undefined;
  /** Generic blocker request */
  end_flow_request?:
    | EndFlowRequest
    | undefined;
  /** P2pInstrumentSelectionBlocker */
  p2p_instrument_selection_request?:
    | P2pInstrumentSelectionRequest
    | undefined;
  /** HumanConfirmationBlocker */
  human_confirmation_request?:
    | HumanConfirmationRequest
    | undefined;
  /** CalendarBlocker */
  set_date_request?:
    | SetDateRequest
    | undefined;
  /** ClabeEntryBlocker */
  clabe_entry_request?:
    | ClabeEntryRequest
    | undefined;
  /** MultiCurrencyAmountEntryBlocker */
  multi_currency_amount_entry_request?:
    | MultiCurrencyAmountEntryRequest
    | undefined;
  /** MultiCurrencyPaymentReviewBlocker */
  multi_currency_payment_review_request?:
    | MultiCurrencyPaymentReviewRequest
    | undefined;
  /** OverflowOptionPickerBlocker */
  overflow_option_picker_request?:
    | OverflowOptionPickerRequest
    | undefined;
  /** TransferOptionSelectionBlocker */
  select_transfer_option_request?:
    | SelectTransferOptionRequest
    | undefined;
  /** BiometricsOptinPrimerBlocker */
  submit_biometrics_optin_request?:
    | SubmitBiometricsOptinRequest
    | undefined;
  /** RequestPushNotificationsBlocker */
  request_push_notifications_request?:
    | RequestPushNotificationsRequest
    | undefined;
  /** FidesmoProvisioningBlocker */
  submit_fidesmo_provisioning_blocker_request?:
    | SubmitFidesmoProvisioningBlockerRequest
    | undefined;
  /** PaymentDeviceCustomizationBlocker */
  payment_device_customization_request?:
    | PaymentDeviceCustomizationRequest
    | undefined;
  /** ProveMobileAuthBlocker */
  submit_prove_mobile_auth_blocker_request?:
    | SubmitProveMobileAuthBlockerRequest
    | undefined;
  /** BitcoinP2pConversionPercentageBlocker */
  submit_bitcoin_p2p_conversion_percentage_request?:
    | SubmitBitcoinP2pConversionPercentageRequest
    | undefined;
  /** AddMoneyBlocker */
  add_money_request?:
    | AddMoneyRequest
    | undefined;
  /** LiteRecoveryEmailOtpBlocker */
  submit_lite_recovery_email_otp_blocker_request?:
    | SubmitLiteRecoveryEmailOtpBlockerRequest
    | undefined;
  /** CashLiteApplePayPushProvisioningBlocker */
  cash_lite_apple_pay_push_provisioning_inputs?:
    | CashLiteApplePayPushProvisioningInputs
    | undefined;
  /** CashLiteGooglePayPushProvisioningBlocker */
  cash_lite_google_pay_push_provisioning_inputs?:
    | CashLiteGooglePayPushProvisioningInputs
    | undefined;
  /** PasskeyAuthBlocker */
  submit_passkey_auth_blocker_request?:
    | SubmitPasskeyAuthBlockerRequest
    | undefined;
  /** GenieBlocker */
  submit_genie_blocker_request?: SubmitGenieBlockerRequest | undefined;
}

/** Submitted when a Genie action completes a GenieBlocker. */
export interface SubmitGenieBlockerRequest {
  /** Full Genie action ID that completed the blocker. */
  action_id?: string;
  /** Submission view state after applying the action's request value mappings. */
  view_state?: KeyValueMap;
}

/**
 * EXPERIMENTAL: Please do not use this proto (SubmitBlockerResponse) as it is part of an ongoing experiment.
 * Unannounced changes will be continually made to this proto.
 *
 * Contains the next blocker(s) in the flow, stored in
 * flow_response_context.scenario_plan.blocker_descriptors
 */
export interface SubmitBlockerResponse {
  app_response_context?: SubmitBlockerResponseAppResponseContext;
  blocker_response_context?: SubmitBlockerResponseBlockerResponseContext;
  flow_response_context?: SubmitBlockerResponseFlowResponseContext;
}

/** Server-provided information that the client might use to update its local context. */
export interface SubmitBlockerResponseAppResponseContext {
  /** An updated profile generated by the server request */
  profile?: Profile;
  /** New sync entities generated by the server request */
  sync_entities_data?: SyncEntitiesResponse;
}

/** Additional information about the flow aside from the next blocker. */
export interface SubmitBlockerResponseBlockerResponseContext {
  dialog?: SubmitBlockerResponseBlockerResponseContextDialog;
}

/**
 * Indicates to the client that a modal dialog should be shown.
 *
 * TODO (doh): promote this proto to a shared definition
 */
export interface SubmitBlockerResponseBlockerResponseContextDialog {
  title?: string;
  message?: string;
}

/**
 * Information about the current state of the flow.
 * Contains the next blocker(s) that the client should show.
 */
export interface SubmitBlockerResponseFlowResponseContext {
  scenario_plan?: ScenarioPlan;
  status_result?: StatusResult;
}
